import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { ChefHat, CheckCircle2, Clock3, Leaf, PackageCheck, Sparkles } from 'lucide-react';
import WakeLockToggle from './WakeLockToggle';
import { devLog } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerBoard() {
  const [orders, setOrders] = useState([]);
  const [waitTime, setWaitTime] = useState(null);

  const fetchOrders = useCallback(async () => {
    try { const res = await fetch(`${API}/board/orders`); setOrders(await res.json()); } catch (err) { if (process.env.NODE_ENV === 'development') devLog.error('Customer board orders fetch failed:', err); }
  }, []);
  const fetchWaitTime = useCallback(async () => {
    try { const res = await fetch(`${API}/board/wait-time`); setWaitTime(await res.json()); } catch (err) { if (process.env.NODE_ENV === 'development') devLog.error('Customer board wait time fetch failed:', err); }
  }, []);

  useEffect(() => {
    fetchOrders(); fetchWaitTime();
    const i1 = setInterval(fetchOrders, 10000);
    const i2 = setInterval(fetchWaitTime, 15000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [fetchOrders, fetchWaitTime]);

  const onWsMessage = useCallback((msg) => {
    if (['new_order', 'order_status', 'order_cancelled', 'daily_reset'].includes(msg.type)) { fetchOrders(); fetchWaitTime(); }
  }, [fetchOrders, fetchWaitTime]);
  useWebSocket(onWsMessage);

  const preparing = orders.filter(o => ['new', 'in_progress'].includes(o.status));
  const ready = orders.filter(o => o.status === 'ready');

  const itemSummary = (items) => {
    if (!items || items.length === 0) return '';
    return items.map(i => `${i.qty}x ${i.name}`).join(', ');
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0D1F0D] text-white" data-testid="customer-board">
      <BoardHeader waitTime={waitTime} preparingCount={preparing.length} readyCount={ready.length} />

      <main className="flex-1 p-4 sm:p-6" data-testid="customer-board-main">
        <div className="grid min-h-full grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
          <StatusColumn
            icon={<ChefHat size={18} />}
            title="Preparing"
            count={preparing.length}
            tone="preparing"
            emptyText="No orders preparing"
            testId="board-preparing-column"
          >
            {preparing.map(order => (
              <BoardOrderCard key={order.id} order={order} status="preparing" summary={itemSummary(order.items)} />
            ))}
          </StatusColumn>

          <StatusColumn
            icon={<CheckCircle2 size={18} />}
            title="Ready for Pickup"
            count={ready.length}
            tone="ready"
            emptyText="No orders ready"
            testId="board-ready-column"
          >
            {ready.map(order => (
              <BoardOrderCard key={order.id} order={order} status="ready" summary={itemSummary(order.items)} />
            ))}
          </StatusColumn>
        </div>
      </main>

      <BoardFooter />
    </div>
  );
}

function BoardHeader({ waitTime, preparingCount, readyCount }) {
  return (
    <header className="border-b border-[#4A7C3F]/25 bg-[#0A190A]/95 px-4 py-4 shadow-[0_10px_35px_rgba(0,0,0,0.25)] backdrop-blur-sm sm:px-6" data-testid="board-header">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-[#D4CFC5] shadow-sm" data-testid="board-brand-mark"><Leaf size={26} /></span>
          <div>
            <h1 className="font-brand text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="board-brand-title">Wildcraft Gatherings</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CAF91]" data-testid="board-subtitle">Order Status</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
          <WakeLockToggle testIdPrefix="board-wake-lock" />
          <BoardMiniStat icon={<ChefHat size={13} />} label="Preparing" value={preparingCount} testId="board-preparing-count" />
          <BoardMiniStat icon={<PackageCheck size={13} />} label="Ready" value={readyCount} testId="board-ready-count" />
          {waitTime && waitTime.active_orders > 0 && <WaitTimePill waitTime={waitTime} />}
        </div>
      </div>
    </header>
  );
}

function BoardMiniStat({ icon, label, value, testId }) {
  return (
    <div className="flex min-h-12 items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2" data-testid={testId}>
      <span className="text-[#D4CFC5]">{icon}</span>
      <div>
        <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[#9CAF91]">{label}</div>
        <div className="font-mono text-xl font-bold leading-none text-white">{value}</div>
      </div>
    </div>
  );
}

function WaitTimePill({ waitTime }) {
  return (
    <div className="flex min-h-14 items-center gap-3 rounded-2xl border border-[#B8860B]/45 bg-[#1B3A1A] px-4 py-2 text-right shadow-sm" data-testid="board-wait-time">
      <Clock3 size={18} className="text-[#B8860B]" />
      <div>
        <div className="font-mono text-3xl font-bold leading-none text-white">~{waitTime.estimated_wait_minutes}<span className="ml-1 text-lg text-[#9CAF91]">min</span></div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#9CAF91]">Est. Wait</div>
      </div>
    </div>
  );
}

function StatusColumn({ icon, title, count, tone, emptyText, testId, children }) {
  const isReady = tone === 'ready';
  return (
    <section className={`overflow-hidden rounded-3xl border shadow-[0_18px_45px_rgba(0,0,0,0.2)] ${isReady ? 'border-[#4A7C3F]/45 bg-[#142814]' : 'border-[#B8860B]/30 bg-[#101F10]'}`} data-testid={testId}>
      <div className={`flex min-h-16 items-center justify-between gap-3 border-b px-4 py-4 sm:px-5 ${isReady ? 'border-[#4A7C3F]/25 bg-[#1B3A1A]/65' : 'border-[#B8860B]/20 bg-[#0D1F0D]/75'}`} data-testid={`${testId}-header`}>
        <div className="flex items-center gap-3">
          <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${isReady ? 'bg-[#E8F0E4] text-[#1B3A1A]' : 'bg-[#FFF8E1] text-[#8A6500]'}`}>{icon}</span>
          <div>
            <h2 className="font-heading text-xl font-bold leading-tight text-white sm:text-2xl">{title}</h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#9CAF91]">{count} order{count === 1 ? '' : 's'}</p>
          </div>
        </div>
        <span className={`flex h-12 min-w-12 items-center justify-center rounded-2xl px-3 font-mono text-2xl font-bold ${isReady ? 'bg-[#4A7C3F] text-white' : 'bg-[#B8860B] text-white'}`} data-testid={`${testId}-count`}>{count}</span>
      </div>
      <div className="space-y-3 p-4 sm:p-5" data-testid={`${testId}-orders`}>
        {count > 0 ? children : <EmptyState text={emptyText} tone={tone} testId={`${testId}-empty`} />}
      </div>
    </section>
  );
}

function BoardOrderCard({ order, status, summary }) {
  const isReady = status === 'ready';
  const cooking = order.status === 'in_progress';
  const testId = isReady ? `board-ready-${order.id}` : `board-preparing-${order.id}`;

  return (
    <article
      data-testid={testId}
      className={`animate-slide-in rounded-3xl border p-4 shadow-[0_10px_30px_rgba(0,0,0,0.18)] transition-transform duration-300 sm:p-5 ${isReady ? 'border-[#4A7C3F]/60 bg-[#F5FAF3] text-[#102A12] animate-ready-pop' : cooking ? 'border-[#B8860B]/70 bg-[#1B3A1A] text-white' : 'border-white/10 bg-white/8 text-white'}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-heading font-bold leading-tight tracking-tight" style={{ fontSize: 'clamp(1.8rem, 5vw, 3.5rem)' }} data-testid={`${testId}-customer-name`}>{order.customer_name}</div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <BoardDetail label="Order" value={`#${order.order_number}`} muted={!isReady} testId={`${testId}-order-number`} />
            {order.table_number && <BoardDetail label="Table" value={`#${order.table_number}`} highlight={!isReady} muted={isReady} testId={`${testId}-table-number`} />}
          </div>
        </div>
        <StatusBadge isReady={isReady} cooking={cooking} testId={`${testId}-status`} />
      </div>

      {summary && <div className={`mt-3 rounded-2xl px-3 py-2 font-mono text-sm ${isReady ? 'bg-white text-[#4A5D4E]' : 'bg-white/8 text-[#9CAF91]'}`} data-testid={`${testId}-items`}>{summary}</div>}
      {cooking && <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#FFF8E1] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#8A6500]" data-testid={`${testId}-cooking-now`}><ChefHat size={12} /> Cooking Now</div>}
    </article>
  );
}

function BoardDetail({ label, value, highlight = false, muted = false, testId }) {
  return (
    <div data-testid={testId}>
      <div className={`font-mono text-[9px] uppercase tracking-wider ${muted ? 'text-[#7D8B69]' : highlight ? 'text-[#B8860B]/80' : 'text-white/45'}`}>{label}</div>
      <span className={`font-mono text-lg font-bold ${highlight ? 'text-[#B8860B]' : muted ? 'text-[#7D8B69]' : 'text-white/70'}`}>{value}</span>
    </div>
  );
}

function StatusBadge({ isReady, cooking, testId }) {
  if (isReady) {
    return <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#4A7C3F] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white" data-testid={testId}><CheckCircle2 size={12} /> Ready</span>;
  }
  if (cooking) {
    return <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#FFF8E1] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#8A6500]" data-testid={testId}><ChefHat size={12} /> Cooking</span>;
  }
  return <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-[#9CAF91]" data-testid={testId}><Sparkles size={12} /> Queued</span>;
}

function EmptyState({ text, tone, testId }) {
  const isReady = tone === 'ready';
  return (
    <div className={`rounded-3xl border border-dashed py-10 text-center font-mono text-sm font-bold uppercase tracking-wider ${isReady ? 'border-[#4A7C3F]/35 bg-[#1B3A1A]/35 text-[#9BCB91]' : 'border-[#B8860B]/35 bg-[#1B3A1A]/25 text-[#D9B66F]'}`} data-testid={testId}>
      {text}
    </div>
  );
}

function BoardFooter() {
  return (
    <footer className="flex items-center justify-between border-t border-[#4A7C3F]/25 bg-[#0A190A] px-6 py-3" data-testid="board-footer">
      <span className="font-mono text-xs uppercase tracking-wider text-[#4A7C3F]/60" data-testid="board-footer-brand">Wildcraft Gatherings</span>
      <span className="font-mono text-xs text-[#4A7C3F]/60" data-testid="board-footer-time">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
    </footer>
  );
}

